import { useState, useEffect, useRef } from "react";
import { MagnifyingGlass, MapPin, X } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchBar({ onSelectLocation }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/geocode`, { params: { q: query } });
        setResults(res.data.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (result) => {
    onSelectLocation({ lat: result.lat, lon: result.lon, name: result.name });
    setQuery(result.name.split(",")[0]);
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="flex items-center bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5 focus-within:border-emerald-600 transition-colors">
        <MagnifyingGlass size={14} className="text-stone-400 mr-2 shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search location..."
          className="bg-transparent text-white text-xs w-28 sm:w-44 focus:outline-none placeholder:text-stone-500"
          data-testid="map-search-input"
        />
        {loading && (
          <div className="w-3 h-3 border border-stone-500 border-t-emerald-400 rounded-full animate-spin mr-1" />
        )}
        {query && !loading && (
          <button
            onClick={() => {
              setQuery("");
              setResults([]);
              setOpen(false);
            }}
            data-testid="search-clear-btn"
          >
            <X size={12} className="text-stone-400 hover:text-white" />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div
          className="absolute top-full left-0 mt-2 w-80 bg-stone-900 border border-stone-700 rounded-xl shadow-2xl overflow-hidden z-[1100]"
          data-testid="search-results-dropdown"
        >
          {results.map((r, i) => (
            <button
              key={i}
              onClick={() => handleSelect(r)}
              className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-stone-800 transition-colors border-b border-stone-800/50 last:border-0"
              data-testid={`search-result-${i}`}
            >
              <MapPin size={16} className="text-emerald-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{r.name}</p>
                <p className="text-[10px] text-stone-500">
                  {r.lat.toFixed(4)}, {r.lon.toFixed(4)}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
